import { ChangeEvent, useContext, useEffect, useState } from "react";
import "./FastBoundErrorLog.scss";
import Modal from "react-modal";
import OwButton from "../../../../shared/OwButton/OwButton";
import OwLabel from "../../../../shared/OwLabel/OwLabel";
import OwSelectOption from "../../../../shared/OwSelectOption/OwSelectOption";
import OwCheckbox from "../../../../shared/OwCheckbox/OwCheckbox";
import { PaginationBar } from "../../../../shared/PaginationBar/PaginationBar";
import { OwFnTable } from "../../../../shared/OwFnTable/OwFnTable";
import IOwTableCols from "../../../../../models/IOwTableCols";
import IFastbound_Account from "../../../../../models/IFastbound_Account";
import IFastbound_Error_Log from "../../../../../models/IFastbound_Error_Log";
import IErrorDTO from "../../../../../models/DTO/IErrorDTO";
import INotification from "../../../../../models/INotification";
import NotificationLevelEnum from "../../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../../models/NotificationPositionEnum";
import UserContext from "../../../../../contexts/UserContext";
import SupplierContext from "../../../../../contexts/SupplierContext";
import FastBoundService from "../../../../../services/FastBoundService";
import UserService from "../../../../../services/UserService";
import { FastBoundErrorType } from "../../../../../models/INewFastbound_Error_Log";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import OwBadge from "@components/shared/OwBadge/OwBadge";

type Props = {
    accounts: IFastbound_Account[];
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    onErrorDismissed?: () => void;
};

const severityBadge: Record<string, string> = {
    error: "danger",
    warning: "warning",
    info: "info",
};

const errorTypeOptions = [
    { value: "sync_acquisitions", title: "Sync Acquisitions" },
    { value: "sync_contacts", title: "Sync Contacts" },
    { value: "sync_items", title: "Sync Items" },
    { value: "create_disposition", title: "Create Disposition" },
    { value: "api_error", title: "API Error" },
];

const getModalStyle = () => ({
    content: {
        top: "10px",
        left: "10px",
        right: "10px",
        bottom: "10px",
        color: "var(--main-text-color)",
        backgroundColor: "var(--main-bg-color)",
        border: "1px solid var(--minor-bg-color)",
    },
    overlay: {
        zIndex: 100,
    },
});

export const FastBoundErrorLog = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
    const [logs, setLogs] = useState<IFastbound_Error_Log[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [filterType, setFilterType] = useState<FastBoundErrorType | "">("");
    const [filterDismissed, setFilterDismissed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [actionActiveId, setActionActiveId] = useState<number | null>(null);
    const [detailModalIsOpen, setDetailModalIsOpen] = useState(false);
    const [detailLog, setDetailLog] = useState<IFastbound_Error_Log | null>(null);
    const [isDismissingAll, setIsDismissingAll] = useState(false);
    const [retryQueueModalIsOpen, setRetryQueueModalIsOpen] = useState(false);
    const [retryQueueLog, setRetryQueueLog] = useState<IFastbound_Error_Log | null>(null);
    const [isRetryingQueue, setIsRetryingQueue] = useState(false);

    useEffect(() => {
        if (props.accounts.length > 0 && !selectedAccountId) {
            setSelectedAccountId(props.accounts[0].id);
        }
    }, [props.accounts]);

    useEffect(() => {
        if (selectedAccountId) {
            loadErrorLogs();
        }
    }, [selectedAccountId, page, pageSize, filterType, filterDismissed]);

    const loadErrorLogs = async () => {
        if (!selectedAccountId) return;
        setIsLoading(true);

        try {
            const params: any = {
                page,
                pageSize,
                fastbound_account_id: selectedAccountId,
            };
            if (filterType) params.type = filterType;
            if (filterDismissed !== undefined) params.is_dismissed = filterDismissed;

            const data = await FastBoundService.getErrorLogs(params, userModel, supplierModel);
            setLogs(data.rows || []);
            setTotalCount(data.count || 0);
        } catch (err: any) {
            console.error("Failed to load error logs:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAccountChange = (e: ChangeEvent<any>) => {
        const value = e.target.value ? Number(e.target.value) : null;
        setSelectedAccountId(value);
        setPage(1);
    };

    const handleTypeFilterChange = (e: ChangeEvent<any>) => {
        setFilterType(e.target.value || "");
        setPage(1);
    };

    const handleDismissedToggle = (e: ChangeEvent<HTMLInputElement>) => {
        const checked = String(e.target.value) === "true";
        setFilterDismissed(checked);
        setPage(1);
    };

    const handleDismiss = async (errorId: number) => {
        setActionActiveId(errorId);
        try {
            await FastBoundService.dismissErrorLog(errorId, userModel, supplierModel);
            loadErrorLogs();
            props.onErrorDismissed?.();
        } catch (err: any) {
            props.notifyFn(null, {
                message: err.message || "failed_to_dismiss_error",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            });
        } finally {
            setActionActiveId(null);
        }
    };

    const handleRetry = async (errorId: number) => {
        setActionActiveId(errorId);
        try {
            await FastBoundService.retryErrorLog(errorId, userModel, supplierModel);
            props.notifyFn(null, {
                message: "error_requeued_for_retry",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            });
            loadErrorLogs();
        } catch (err: any) {
            props.notifyFn(null, {
                message: err.message || "failed_to_retry_error",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            });
        } finally {
            setActionActiveId(null);
        }
    };

    const handleDismissAll = async () => {
        if (!selectedAccountId) return;
        setIsDismissingAll(true);

        try {
            await FastBoundService.dismissAllErrorLogs(selectedAccountId, userModel, supplierModel);
            props.notifyFn(null, {
                message: "errors_dismissed",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            });
            loadErrorLogs();
            props.onErrorDismissed?.();
        } catch (err: any) {
            props.notifyFn(null, {
                message: err.message || "failed_to_dismiss_errors",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            });
        } finally {
            setIsDismissingAll(false);
        }
    };

    const openRetryQueueModal = (log: IFastbound_Error_Log) => {
        setRetryQueueLog(log);
        setRetryQueueModalIsOpen(true);
    };

    const closeRetryQueueModal = () => {
        setRetryQueueModalIsOpen(false);
        setRetryQueueLog(null);
    };

    const handleRetryQueueItem = async () => {
        if (!retryQueueLog?.fastbound_queue_id) return;
        setIsRetryingQueue(true);
        try {
            await FastBoundService.retryQueueItem(retryQueueLog.fastbound_queue_id, userModel, supplierModel);
            props.notifyFn(null, {
                message: "queue_item_requeued_for_retry",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            });
            closeRetryQueueModal();
            loadErrorLogs();
        } catch (err: any) {
            props.notifyFn(null, {
                title: err.message,
                message: err.description || err.message || "failed_to_retry_queue_item",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
                skipMessageTranslate: true,
            });
        } finally {
            setIsRetryingQueue(false);
        }
    };

    const openDetailModal = (log: IFastbound_Error_Log) => {
        setDetailLog(log);
        setDetailModalIsOpen(true);
    };

    const closeDetailModal = () => {
        setDetailModalIsOpen(false);
        setDetailLog(null);
    };

    const truncateMessage = (message: string, maxLen: number = 80): string => {
        if (!message) return "";
        return message.length > maxLen ? message.substring(0, maxLen) + "..." : message;
    };

    const columns: IOwTableCols[] = [
        {
            title: "",
            valueProp: "id",
            isActionsCol: true,
            renderFn: (row: IFastbound_Error_Log) => (
                <div className={"btn-group btn-group-sm"}>
                    {row.is_retryable && !row.is_dismissed && (
                        <OwButton
                            centerIconClass={"fad fa-redo"}
                            className={"btn-primary"}
                            hoverTitle={"Retry"}
                            fn={() => handleRetry(row.id)}
                            disabled={actionActiveId === row.id}
                        />
                    )}
                    {row.fastbound_queue_id && (
                        <OwButton
                            centerIconClass={"fad fa-sync"}
                            className={"btn-outline-primary"}
                            hoverTitle={"Retry Queue Item"}
                            fn={() => openRetryQueueModal(row)}
                            disabled={actionActiveId === row.id}
                        />
                    )}
                    {!row.is_dismissed && (
                        <OwButton
                            centerIconClass={"fad fa-eye-slash"}
                            className={"btn-warning"}
                            hoverTitle={"Dismiss"}
                            fn={() => handleDismiss(row.id)}
                            disabled={actionActiveId === row.id}
                        />
                    )}
                    <OwButton
                        centerIconClass={"fad fa-info-circle"}
                        className={"btn-info"}
                        hoverTitle={"Details"}
                        fn={() => openDetailModal(row)}
                    />
                </div>
            ),
            renderFnEmitsRow: true,
        },
        {
            title: "severity",
            valueProp: "severity",
            renderFn: (row: IFastbound_Error_Log) => (
                <OwBadge bgColor={`bg-${severityBadge[row.severity || "error"] || "danger"}`} title={row.severity || "error"} />
            ),
            renderFnEmitsRow: true,
        },
        {
            title: "type",
            valueProp: "type",
            className: "small",
            renderFn: (row: IFastbound_Error_Log) => <OwLabel title={row.type} />,
            renderFnEmitsRow: true,
        },
        {
            title: "message",
            valueProp: "message",
            className: "small",
            renderFn: (row: IFastbound_Error_Log) => truncateMessage(row.message),
            renderFnEmitsRow: true,
        },
        {
            title: "entity",
            valueProp: "entity_type",
            className: "small",
            renderFn: (row: IFastbound_Error_Log) =>
                row.entity_type ? (
                    <span>
                        {row.entity_type}
                        {row.entity_id ? `: ${row.entity_id}` : ""}
                    </span>
                ) : null,
            renderFnEmitsRow: true,
        },
        {
            title: "date",
            valueProp: "created_at",
            className: "small",
            renderFn: (row: IFastbound_Error_Log) => (row.created_at ? UserService.parseLastUpdated(row.created_at, true, true, true) : ""),
            renderFnEmitsRow: true,
        },
    ];

    if (props.accounts.length === 0) return null;

    return (
        <div className={"FastBoundErrorLog mt-3"}>
            <div className={"d-flex align-items-center mb-2 flex-wrap"}>
                <OwLabel title={"error_log"} isBold={true} className={"me-3"} />
                <div className={"w-15r me-2"}>
                    <OwSelectOption
                        options={props.accounts}
                        valueProp={"id"}
                        titleProp={"name"}
                        name={"errorLogAccountId"}
                        selectedOption={{ errorLogAccountId: selectedAccountId }}
                        selectedOptionProp={"errorLogAccountId"}
                        fn={handleAccountChange}
                    />
                </div>
                <div className={"w-10r me-2"}>
                    <OwSelectOption
                        options={errorTypeOptions}
                        valueProp={"value"}
                        titleProp={"title"}
                        name={"filterType"}
                        selectedOption={{ filterType }}
                        selectedOptionProp={"filterType"}
                        fn={handleTypeFilterChange}
                        showBlankFirst={true}
                    />
                </div>
                <div className={"me-2"}>
                    <OwToggle
                        name={"filterDismissed"}
                        label={"show_dismissed"}
                        active={filterDismissed}
                        fn={handleDismissedToggle}
                        labelOnRight={true}
                    />
                </div>
                <div className={"me-2"}>
                    <OwButton
                        title={"refresh"}
                        fn={loadErrorLogs}
                        className={"btn-outline-primary btn-sm"}
                        leftIconClass={"fad fa-sync"}
                        active={isLoading}
                        disableOnActive={true}
                    />
                </div>
                <div className={"ms-auto"}>
                    <OwButton
                        title={"dismiss_all"}
                        fn={handleDismissAll}
                        className={"btn-outline-danger btn-sm"}
                        active={isDismissingAll}
                        disableOnActive={true}
                    />
                </div>
            </div>

            <OwFnTable
                columns={columns}
                dataSet={logs}
                dataFetch={isLoading}
                initialData={isLoading && logs.length === 0}
                tableConfig={{ tableSmall: true }}
                rowKeyProp={"id"}
                wrappingClassName={"w-100"}
            />

            <PaginationBar
                offset={page - 1}
                limit={pageSize}
                totalRecords={totalCount}
                onPaginationChanged={(offset, limit) => {
                    setPage(offset + 1);
                    setPageSize(limit);
                }}
            />

            <Modal
                isOpen={detailModalIsOpen}
                onRequestClose={closeDetailModal}
                contentLabel="Error Detail"
                shouldCloseOnOverlayClick={true}
                className={"modalContentClass"}
                overlayClassName={"modalOverlayClass"}
                style={getModalStyle()}>
                {detailLog && (
                    <div className={"p-3"}>
                        <div className={"d-flex justify-content-between mb-3"}>
                            <h5>
                                <OwLabel title={"error_detail"} />
                            </h5>
                            <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={closeDetailModal} />
                        </div>

                        <div className={"mb-2"}>
                            <strong>
                                <OwLabel title={"severity"} />:
                            </strong>{" "}
                            <span className={`badge bg-${severityBadge[detailLog.severity || "error"]}`}>
                                {detailLog.severity || "error"}
                            </span>
                        </div>

                        <div className={"mb-2"}>
                            <strong>
                                <OwLabel title={"type"} />:
                            </strong>{" "}
                            {detailLog.type}
                        </div>

                        <div className={"mb-2"}>
                            <strong>
                                <OwLabel title={"message"} />:
                            </strong>{" "}
                            {detailLog.message}
                        </div>

                        {detailLog.entity_type && (
                            <div className={"mb-2"}>
                                <strong>
                                    <OwLabel title={"entity"} />:
                                </strong>{" "}
                                {detailLog.entity_type}
                                {detailLog.entity_id ? ` (${detailLog.entity_id})` : ""}
                            </div>
                        )}

                        {detailLog.Fastbound_Account && (
                            <div className={"mb-2"}>
                                <strong>
                                    <OwLabel title={"account"} />:
                                </strong>{" "}
                                {detailLog.Fastbound_Account.name}
                            </div>
                        )}

                        <div className={"mb-2"}>
                            <strong>
                                <OwLabel title={"retry_count"} />:
                            </strong>{" "}
                            {detailLog.retry_count || 0}
                            {detailLog.last_retry_at && (
                                <span className={"ms-2 text-muted small"}>
                                    (Last: {UserService.parseLastUpdated(detailLog.last_retry_at as any, true, true, true)})
                                </span>
                            )}
                        </div>

                        <div className={"mb-2"}>
                            <strong>
                                <OwLabel title={"date"} />:
                            </strong>{" "}
                            {detailLog.created_at ? UserService.parseLastUpdated(detailLog.created_at, true, true, true) : ""}
                        </div>

                        {detailLog.details && (
                            <div className={"mb-2"}>
                                <strong>
                                    <OwLabel title={"details"} />:
                                </strong>
                                <pre
                                    className={"mt-1 p-2"}
                                    style={{
                                        backgroundColor: "var(--minor-bg-color)",
                                        borderRadius: "4px",
                                        maxHeight: "400px",
                                        overflow: "auto",
                                        fontSize: "0.85rem",
                                    }}>
                                    {typeof detailLog.details === "string" ? detailLog.details : JSON.stringify(detailLog.details, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            <Modal
                isOpen={retryQueueModalIsOpen}
                onRequestClose={closeRetryQueueModal}
                contentLabel="Retry Queue Item"
                shouldCloseOnOverlayClick={true}
                className={"modalContentClass"}
                overlayClassName={"modalOverlayClass"}
                style={{ ...getModalStyle(), content: { ...getModalStyle().content, minWidth: "auto", maxWidth: "450px" } }}>
                {retryQueueLog && (
                    <div className={"p-3"}>
                        <div className={"d-flex justify-content-between mb-3"}>
                            <h5>
                                <OwLabel title={"retry_queue_item"} />
                            </h5>
                            <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={closeRetryQueueModal} />
                        </div>

                        <p className={"mb-2"}>
                            <OwLabel title={"retry_queue_item_confirm"} />
                        </p>

                        <div className={"mb-2 small"}>
                            <strong>
                                <OwLabel title={"type"} />:
                            </strong>{" "}
                            {retryQueueLog.type}
                        </div>
                        <div className={"mb-3 small"}>
                            <strong>
                                <OwLabel title={"message"} />:
                            </strong>{" "}
                            {truncateMessage(retryQueueLog.message, 120)}
                        </div>

                        <div className={"d-flex justify-content-end"}>
                            <OwButton title={"cancel"} fn={closeRetryQueueModal} className={"btn-outline-secondary me-2"} />
                            <OwButton
                                title={"retry"}
                                fn={handleRetryQueueItem}
                                className={"btn-primary"}
                                leftIconClass={"fad fa-sync"}
                                active={isRetryingQueue}
                                disableOnActive={true}
                            />
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};
